import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@creanizer/ui'
import { Book, Search, Trash2, Copy, Edit2, Calendar, Dice1, Users, Swords, Sparkles, RefreshCw, Download, Upload } from 'lucide-react'
import { useScenarioStore } from '../../../../stores/scenarioStore'
// Toast notifications removed
import { formatDistanceToNow } from 'date-fns'
import { ja } from 'date-fns/locale'

interface SavedSystemsListProps {
  onEdit?: (systemId: string) => void
}

export function SavedSystemsList({ onEdit }: SavedSystemsListProps = {}) {
  const { systems, removeSystem, updateSystem, addSystem } = useScenarioStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [editingSystem, setEditingSystem] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [systemToDelete, setSystemToDelete] = useState<string | null>(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importData, setImportData] = useState('')
  
  const filteredSystems = systems.filter(system => {
    const query = searchQuery.toLowerCase()
    return (
      system.name.toLowerCase().includes(query) ||
      system.result?.systemInfo?.name?.toLowerCase().includes(query) ||
      system.result?.systemInfo?.description?.toLowerCase().includes(query)
    )
  })
  
  const handleCopy = (system: typeof systems[0]) => {
    const jsonData = JSON.stringify(system.result, null, 2)
    navigator.clipboard.writeText(jsonData)
    console.log('システムデータをコピーしました')
  }
  
  const handleEdit = (system: typeof systems[0]) => {
    setEditingSystem(system.id)
    setEditName(system.name)
  }
  
  const handleSaveEdit = (id: string) => {
    if (editName.trim()) {
      const system = systems.find(s => s.id === id)
      if (system) {
        updateSystem(id, { ...system, name: editName.trim() })
        console.log('システム名を更新しました')
      }
    }
    setEditingSystem(null)
    setEditName('')
  }
  
  const handleCancelEdit = () => {
    setEditingSystem(null)
    setEditName('')
  }
  
  const handleDelete = (id: string) => {
    setSystemToDelete(id)
    setDeleteDialogOpen(true)
  }
  
  const confirmDelete = () => {
    if (systemToDelete) {
      removeSystem(systemToDelete)
      console.log('システムを削除しました')
      setSystemToDelete(null)
    }
    setDeleteDialogOpen(false)
  }
  
  // 個別システムのエクスポート
  const handleExportSystem = (system: typeof systems[0]) => {
    const exportData = {
      type: 'creanizer-system',
      version: '1.0',
      exportDate: new Date().toISOString(),
      system: system
    }
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `system_${system.name.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    console.log('システムをエクスポートしました')
  }
  
  // 全システムのエクスポート
  const handleExportAll = () => {
    const exportData = {
      type: 'creanizer-systems-bulk',
      version: '1.0',
      exportDate: new Date().toISOString(),
      systems: systems
    }
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `all_systems_${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    console.log(`${systems.length}個のシステムをエクスポートしました`)
  }
  
  // インポート処理
  const handleImport = () => {
    try {
      const data = JSON.parse(importData)
      if (data.type === 'creanizer-system' && data.system) {
        // 単一システムのインポート
        addSystem({
          ...data.system,
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
        console.log('1個のシステムをインポートしました')
      } else if (data.type === 'creanizer-systems-bulk' && data.systems) {
        // 複数システムのインポート
        data.systems.forEach((system: any) => {
          addSystem({
            ...system,
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
        })
        console.log(`${data.systems.length}個のシステムをインポートしました`)
      } else {
        throw new Error('無効なファイル形式です')
      }
      setImportDialogOpen(false)
      setImportData('')
    } catch (error) {
      console.error('インポートエラー:', error)
      alert('インポートに失敗しました。ファイル形式を確認してください。')
    }
  }
  
  // ファイル選択処理
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setImportData(event.target?.result as string)
      }
      reader.readAsText(file)
    }
  }
  
  const getSystemFeatures = (result: any) => {
    const features = []
    if (result?.coreRules?.resolution) features.push({ icon: Dice1, label: 'ダイス' })
    if (result?.characterCreation) features.push({ icon: Users, label: 'キャラ作成' })
    if (result?.coreRules?.combat) features.push({ icon: Swords, label: '戦闘' })
    if (result?.coreRules?.magic) features.push({ icon: Sparkles, label: '魔法' })
    return features
  }
  
  if (systems.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Book className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">保存されたシステムはありません</p>
          <p className="text-sm text-gray-400 mt-2">
            システムを解析して保存すると、ここに表示されます
          </p>
        </CardContent>
      </Card>
    )
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="システムを検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setImportDialogOpen(true)}
        >
          <Upload className="w-4 h-4 mr-2" />
          インポート
        </Button>
        {systems.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportAll}
          >
            <Download className="w-4 h-4 mr-2" />
            全てエクスポート
          </Button>
        )}
        <Badge variant="secondary">
          {filteredSystems.length} / {systems.length} 件
        </Badge>
      </div>
      
      <ScrollArea className="h-[600px]">
        <div className="space-y-3 pr-4">
          {filteredSystems.map((system) => {
            const features = getSystemFeatures(system.result)
            
            return (
              <Card key={system.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      {editingSystem === system.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(system.id)
                              if (e.key === 'Escape') handleCancelEdit()
                            }}
                            className="h-8"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(system.id)}
                          >
                            保存
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancelEdit}
                          >
                            キャンセル
                          </Button>
                        </div>
                      ) : (
                        <>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Book className="w-4 h-4" />
                            {system.name}
                          </CardTitle>
                          {system.result?.systemInfo?.version && (
                            <p className="text-sm text-gray-500 mt-1">
                              バージョン: {system.result.systemInfo.version}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    {!editingSystem && (
                      /* デスクトップ版: 縦並びボタン */
                      <div className="hidden sm:flex gap-1">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(system.id)}
                            title="解析結果を編集"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(system)}
                          title="名前を編集"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(system)}
                          title="JSONをコピー"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportSystem(system)}
                          title="エクスポート"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(system.id)}
                          title="削除"
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {system.result?.systemInfo?.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                      {system.result.systemInfo.description}
                    </p>
                  )}
                  
                  {/* モバイル版: 横一列のボタン */}
                  {!editingSystem && (
                    <div className="flex sm:hidden gap-1 mb-3">
                      {onEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(system.id)}
                          title="解析編集"
                          className="flex-1"
                        >
                          <RefreshCw className="w-4 h-4" />
                          <span className="ml-1">解析</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(system)}
                        title="編集"
                        className="flex-1"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="ml-1">名前</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(system)}
                        title="コピー"
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4" />
                        <span className="ml-1">コピー</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(system.id)}
                        title="削除"
                        className="flex-1 text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="ml-1">削除</span>
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex gap-2">
                      {features.map((feature, index) => {
                        const Icon = feature.icon
                        return (
                          <Badge key={index} variant="outline">
                            <Icon className="w-3 h-3 mr-1" />
                            {feature.label}
                          </Badge>
                        )
                      })}
                    </div>
                    
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-2 sm:mt-0">
                      <Calendar className="w-3 h-3" />
                      {formatDistanceToNow(new Date(system.createdAt), { 
                        addSuffix: true,
                        locale: ja 
                      })}
                    </div>
                  </div>
                  
                  {system.result?.coreRules?.resolution && (
                    <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                      <span className="font-semibold">判定:</span> {system.result.coreRules.resolution}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </ScrollArea>
      
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>システムを削除しますか？</DialogTitle>
            <DialogDescription>
              この操作は取り消せません。システムデータは完全に削除されます。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
            <Button onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              削除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>システムをインポート</DialogTitle>
            <DialogDescription>
              エクスポートしたJSONファイルを選択するか、JSONデータを直接貼り付けてください。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="file-input">ファイルを選択</Label>
              <Input
                id="file-input"
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="json-input">またはJSONを貼り付け</Label>
              <Textarea
                id="json-input"
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="JSONデータを貼り付けてください..."
                rows={10}
                className="mt-2 font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setImportDialogOpen(false)
              setImportData('')
            }}>
              キャンセル
            </Button>
            <Button onClick={handleImport} disabled={!importData}>
              インポート
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}